import React, { useState, useEffect, useCallback } from 'react';
import { announcementAPI, dateGenAPI } from '../api';
import Card, { CardBody } from '../components/ui/Card';
import ImageWithDownload from '../components/ImageWithDownload';
import GiftNotification from '../components/gifts/GiftNotification';
import { useWishes } from '../context/WishesContext';
import './HomePage.css';

function HomePage() {
    const [announcements, setAnnouncements] = useState([]);
    const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
    const [announcementsLoading, setAnnouncementsLoading] = useState(true);
    const [showImagePreview, setShowImagePreview] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);

    // Fragment collection state
    const [collectibleFragments, setCollectibleFragments] = useState(null);
    const [collecting, setCollecting] = useState(false);
    const [collectMessage, setCollectMessage] = useState('');

    // Fetch wishes once on home page mount
    const { fetchWishes } = useWishes();

    const fetchCollectible = useCallback(async () => {
        try {
            const response = await dateGenAPI.getStatus();
            setCollectibleFragments(response.data.collectible_fragments);
        } catch {
            // silently fail
        }
    }, []);

    useEffect(() => {
        fetchAnnouncements();
        fetchWishes();
        fetchCollectible();
    }, [fetchWishes, fetchCollectible]);

    const fetchAnnouncements = async () => {
        try {
            const response = await announcementAPI.getPublished();
            const data = response.data;
            setAnnouncements(data);
            // Select the most recent announcement by default
            if (data.length > 0) {
                setSelectedAnnouncement(data[0]);
            }
        } catch (error) {
            // Silently fail - announcements section won't show
        } finally {
            setAnnouncementsLoading(false);
        }
    };

    const getCategoryIcon = (category) => {
        switch (category) {
            case 'feature': return '✨';
            case 'bugfix': return '🔧';
            case 'wish': return '💫';
            case 'theme': return '🎨';
            case 'video_wish': return '🎬';
            case 'style': return '🖌️';
            case 'characteristic': return '🧬';
            default: return '📢';
        }
    };

    const getCategoryColor = (category) => {
        switch (category) {
            case 'feature': return 'var(--color-success)';
            case 'bugfix': return 'var(--color-warning)';
            case 'wish': return 'var(--color-info)';
            case 'theme': return 'var(--color-accent)';
            case 'video_wish': return '#9b59b6';
            case 'style': return '#e67e22';
            case 'characteristic': return '#1abc9c';
            default: return 'var(--color-primary)';
        }
    };

    const getCategoryLabel = (category) => {
        switch (category) {
            case 'feature': return 'New Features';
            case 'bugfix': return 'Bug Fixes';
            case 'wish': return 'New Wishes';
            case 'theme': return 'New Themes';
            case 'video_wish': return 'New Video Wishes';
            case 'style': return 'New Styles';
            case 'characteristic': return 'New Characteristics';
            default: return 'Update';
        }
    };

    // Get all unique categories from an announcement's sections
    const getAnnouncementCategories = (announcement) => {
        if (!announcement.sections) return [];
        return [...new Set(announcement.sections.map(s => s.category))];
    };

    const handleCollectFragments = async () => {
        setCollecting(true);
        try {
            const response = await dateGenAPI.collectFragments();
            setCollectMessage(`Collected ${response.data.fragments_granted} random fragments!`);
            setCollectibleFragments(null);
            setTimeout(() => setCollectMessage(''), 5000);
        } catch (err) {
            setCollectMessage(err.response?.data?.error || 'Failed to collect fragments.');
            setTimeout(() => setCollectMessage(''), 5000);
        } finally {
            setCollecting(false);
        }
    };

    return (
        <div className="home-page animate-fade-in">
            {/* Gift Notification Banner */}
            <GiftNotification />

            {/* Fragment Collection Banner */}
            {collectibleFragments && (
                <div className="collect-fragments-banner">
                    <div className="collect-fragments-content">
                        <div className="collect-fragments-icon">💎</div>
                        <div className="collect-fragments-text">
                            <strong>
                                {collectibleFragments.unused_gens} unused generations from last week!
                            </strong>
                            <p>Collect {collectibleFragments.fragments} random fragments before they expire</p>
                        </div>
                    </div>
                    <button
                        className="collect-fragments-btn"
                        onClick={handleCollectFragments}
                        disabled={collecting}
                    >
                        {collecting ? 'Collecting...' : 'Collect Fragments'}
                    </button>
                </div>
            )}
            {collectMessage && (
                <div className="collect-fragments-message">
                    {collectMessage}
                </div>
            )}

            {/* Announcements Section */}
            {!announcementsLoading && announcements.length > 0 && (
                <div className="announcements-section">
                    <h2 className="section-title">Latest Announcements</h2>
                    <div className="announcements-container">
                        {/* Scrollable list of announcements */}
                        <div className="announcements-list">
                            {announcements.map((announcement) => {
                                const categories = getAnnouncementCategories(announcement);
                                return (
                                    <div
                                        key={announcement.id}
                                        className={`announcement-item ${selectedAnnouncement?.id === announcement.id ? 'selected' : ''}`}
                                        onClick={() => setSelectedAnnouncement(announcement)}
                                        style={{ '--category-color': categories[0] ? getCategoryColor(categories[0]) : 'var(--color-primary)' }}
                                    >
                                        <div className="announcement-item-header">
                                            <div className="announcement-icons">
                                                {categories.map((cat, i) => (
                                                    <span key={i} className="announcement-icon">{getCategoryIcon(cat)}</span>
                                                ))}
                                            </div>
                                        </div>
                                        <h4 className="announcement-item-title">{announcement.title}</h4>
                                        <span className="announcement-date">
                                            {new Date(announcement.published_at).toLocaleDateString()}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Selected announcement detail */}
                        {selectedAnnouncement && (
                            <Card variant="glass" className="announcement-detail">
                                <CardBody>
                                    <div className="announcement-detail-header">
                                        <div className="announcement-detail-icons">
                                            {getAnnouncementCategories(selectedAnnouncement).map((cat, i) => (
                                                <span key={i} className="announcement-detail-icon" style={{ color: getCategoryColor(cat) }}>
                                                    {getCategoryIcon(cat)}
                                                </span>
                                            ))}
                                        </div>
                                        <div>
                                            <h3 className="announcement-detail-title">{selectedAnnouncement.title}</h3>
                                            <span className="announcement-detail-date">
                                                Published on {new Date(selectedAnnouncement.published_at).toLocaleDateString()}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Render each section */}
                                    {selectedAnnouncement.sections && selectedAnnouncement.sections.map((section, idx) => (
                                        <div key={idx} className="announcement-section">
                                            <div className="section-header">
                                                <span className="section-icon" style={{ color: getCategoryColor(section.category) }}>
                                                    {getCategoryIcon(section.category)}
                                                </span>
                                                <h4 className="section-title">{getCategoryLabel(section.category)}</h4>
                                            </div>
                                            <div className="section-content">
                                                {section.content.split('\n').map((line, i) => (
                                                    <p key={i}>{line || '\u00A0'}</p>
                                                ))}
                                            </div>

                                            {section.featured_dates_images && section.featured_dates_images.length > 0 && (
                                                <div className="announcement-featured-images">
                                                    {section.featured_dates_images.map((imageUrl, imgIdx) => (
                                                        <div
                                                            key={imgIdx}
                                                            className="announcement-featured-image"
                                                            style={{ cursor: 'zoom-in' }}
                                                            onClick={() => {
                                                                setPreviewImage(imageUrl);
                                                                setShowImagePreview(true);
                                                            }}
                                                        >
                                                            <ImageWithDownload
                                                                src={imageUrl}
                                                                alt={`Featured ${imgIdx + 1}`}
                                                                style={{ width: '100%' }}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {section.featured_videos_urls && section.featured_videos_urls.length > 0 && (
                                                <div className="announcement-featured-videos">
                                                    {section.featured_videos_urls.map((videoUrl, vidIdx) => (
                                                        <div
                                                            key={vidIdx}
                                                            className="announcement-featured-video"
                                                        >
                                                            <video
                                                                src={videoUrl}
                                                                controls
                                                                style={{ width: '100%', borderRadius: '8px' }}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </CardBody>
                            </Card>
                        )}
                    </div>
                </div>
            )}

            {/* Image Preview Modal */}
            {showImagePreview && previewImage && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.95)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000,
                        padding: '20px',
                        cursor: 'pointer'
                    }}
                    onClick={() => setShowImagePreview(false)}
                >
                    <div style={{ maxWidth: '90vw', maxHeight: '90vh', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ImageWithDownload
                            src={previewImage}
                            alt="Announcement Preview"
                            style={{ maxWidth: '90vw', maxHeight: '90vh', width: 'auto', height: 'auto' }}
                            imgStyle={{ width: 'auto', height: 'auto', maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }}
                            filename={`announcement_${Date.now()}.png`}
                        />
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowImagePreview(false);
                            }}
                            style={{
                                position: 'absolute',
                                top: '10px',
                                left: '10px',
                                background: 'rgba(255, 255, 255, 0.9)',
                                border: 'none',
                                borderRadius: '50%',
                                width: '40px',
                                height: '40px',
                                cursor: 'pointer',
                                fontSize: '1.2rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s',
                                color: '#333'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.background = 'rgba(255, 255, 255, 1)';
                                e.target.style.transform = 'scale(1.1)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = 'rgba(255, 255, 255, 0.9)';
                                e.target.style.transform = 'scale(1)';
                            }}
                            title="Close"
                        >
                            ✕
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default HomePage;
